import { makeAutoObservable } from 'mobx';
import { Row } from '@tanstack/react-table';
import { IPublisherLink } from '../../models/link.model';
import { ILinksApiCallError, LinksApi } from '../../apis/links.api';
import { isAbortError } from '@monorepo/tools/src/lib/tools/ask/guards';
import { IAskError } from '@monorepo/tools/src/lib/tools/ask/ask';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { SettingsStore } from '@monorepo/controlled/src/stores/settings.store';
import { FilterStore } from '@monorepo/controlled/src/stores/filter.store';
import { TableStore } from '@monorepo/controlled/src/stores/table.store';
import { filterPrototypesByProp } from '../../components/filters';

export class LinksStore {
	links: IPublisherLink[] = [];
	isLoading = false;
	error: string | null = null;
	sortedRows: Row<IPublisherLink>[] = [];
	lastFetchedAt = 0;

	settingsStore: SettingsStore;

	constructor(settingsStore: SettingsStore) {
		makeAutoObservable(this);
		this.settingsStore = settingsStore;
	}

	/**
	 * Ensures My Links filters exist on the same settings page key as {@link useRoute}
	 * {@code currentRouteWithoutSpecialChars} and {@link ControlledTable} {@code currentRoute}.
	 */
	ensureFiltersForRoute(routeKey: string): void {
		if (!routeKey) {
			return;
		}
		if (!this.settingsStore.getPages().get(routeKey)) {
			this.settingsStore.getPages().set(routeKey, { tableStore: new TableStore() });
		}
		const page = this.settingsStore.getPages().get(routeKey);
		const tableStore = page?.tableStore;
		if (!tableStore) {
			return;
		}
		if (!tableStore.getFiltersStore(routeKey)) {
			const pageFilterStore = new FilterStore({
				key: routeKey,
				currentFilters: undefined,
				filterPrototypesByProp,
			});
			tableStore.setFiltersStore(routeKey, pageFilterStore);
		}
	}

	setLastFetchedAt(ts: number) {
		this.lastFetchedAt = ts;
	}

	async fetchMyLinks(accountId: id) {
		this.isLoading = true;
		this.error = null;
		try {
			const data = await LinksApi.myLinks({ accountId });
			this.links = this.normalizeLinks(data);
			this.lastFetchedAt = Date.now();
		} catch (err) {
			if (isAbortError(err as IAskError)) {
				return;
			}
			const msg = typeof (err as ILinksApiCallError)?.message === 'string' ? (err as ILinksApiCallError).message.trim() : '';
			this.error = msg || 'Failed to load links.';
		} finally {
			this.isLoading = false;
		}
	}

	/**
	 * Quietly refreshes the links list without triggering a loading state so there is
	 * no visual flicker. Used by the stale-data interval in {@code useMyLinksList}.
	 */
	async silentFetchMyLinks(accountId: id) {
		try {
			const data = await LinksApi.myLinks({ accountId });
			this.links = this.normalizeLinks(data);
			this.lastFetchedAt = Date.now();
		} catch (err) {
			if (isAbortError(err as IAskError)) {
				return;
			}
			// Intentionally silent — do not surface errors for background refreshes.
		}
	}

	private normalizeLinks(data: IPublisherLink[]): IPublisherLink[] {
		return data.map(item => ({
			...item,
			trackingUrl: item.trackingUrl != null && String(item.trackingUrl).trim() !== '' ? String(item.trackingUrl) : '',
		}));
	}

	setLinks(links: IPublisherLink[]) {
		this.links = links;
	}

	getLinks() {
		return this.links;
	}

	getSortedRows(): Row<IPublisherLink>[] {
		return this.sortedRows;
	}

	setSortedRows(sortedRows: Row<IPublisherLink>[]): void {
		this.sortedRows = sortedRows;
	}
}
